import { providerLogos } from 'common/services/sensors.service';
import InlineEdit from 'components/InlineEdit';
import { SPACES } from 'common/constants/sizes';
import {
  updateIntegrationName,
  validateIntegrationName,
  INTEGRATION_STATES
} from './integrations.service';
import Text, { Inline } from 'components/base/Text';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import { ACCOUNT_TYPES_MAP } from '@linearb/linjs-common';
import { Tooltip } from '@linearb/linui-common/base';
import { Flex } from 'components/base/layout-components';
import withGitIntegrationsModals from '../gitIntegrations/withGitIntegrationsModals';
import GitStreamConnectionState from 'views/root/views/home/views/settings/views/RepositoriesV2/gitStreamIntegration/GitStreamConnectionState';
import AutoMonitoredIndicator from './MonitoringRules/AutoMonitoredIndicator';
import useAutoRepoDiscoveryFF from './useAutoRepoDiscoveryFF';
import { AlertIcon, lightColorPalette } from '@linearb/linui-common';
import { Stack } from '@mui/material';
import { LBButton } from '@linearb/buzz';
import Icon from 'components/base/Icon';

const Separator = styled(Inline)`
  color: ${getColor('pigeonPost')};
  padding: 0 0.6rem;
`;

const GitProviderDescription = ({
  sensorName,
  name: integrationName,
  integrationId,
  gitIntegrations,
  updateSensorAuthorization,
  readonly = false
}) => {
  const Logo = providerLogos[sensorName];

  return (
    <>
      <Logo className='size-4 fill-black' />
      <Inline
        pl={SPACES['x-small']}
        onClick={e => {
          if (!readonly) e.stopPropagation();
        }}
      >
        {/* @ts-ignore */}
        <InlineEdit
          readonly={readonly}
          callback={updateIntegrationName(
            integrationId,
            gitIntegrations,
            updateSensorAuthorization
          )}
          validate={validateIntegrationName}
          size='large'
          value={integrationName}
          placeholder='Integration Name'
          dataCy='integration-title'
        />
      </Inline>
    </>
  );
};

const IntegrationState = ({ stateOK, repositoriesNumber }) => {
  if (!stateOK) {
    return (
      <Text color='torchRed' mr='1rem'>
        {INTEGRATION_STATES.NOT_ACTIVE}
      </Text>
    );
  }
  return (
    <Text
      {...(repositoriesNumber ? {} : { color: 'torchRed' })}
      data-cy='repo-count'
    >
      {`${repositoriesNumber} ${
        repositoriesNumber === 1 ? 'Repository' : 'Repositories'
      }`}
    </Text>
  );
};

const IntegrationURL = ({ url }) => {
  if (!url) {
    return null;
  }

  return (
    <>
      <Separator>|</Separator>
      <Text mr='1rem'>{url}</Text>
    </>
  );
};

const IntegrationReauthorize = withGitIntegrationsModals(
  ({
    account,
    stateOK,
    integrationId,
    sensor_name,
    url,
    gitIntegrations,
    toggleModal,
    openReauthorizeModal
  }) => {
    if (account.type_id === ACCOUNT_TYPES_MAP.Primary && !stateOK) {
      return (
        <LBButton
          variant='outline'
          size='sm'
          onClick={e => {
            openReauthorizeModal({
              id: integrationId,
              sensor_name,
              url,
              gitIntegrations,
              toggleModal
            });
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          Reauthorize
        </LBButton>
      );
    }

    return null;
  }
);

export const IntegrationDescription = ({
  sensor_name,
  name: providerName,
  integrationId,
  gitIntegrations,
  updateSensorAuthorization,
  stateOK,
  account,
  url,
  repositoriesNumber,
  toggleModal,
  autoRepoDiscoveryConfig,
  showNotConnectedToGitStream,
  showIsConnectedToGitStream,
  isMultiTokenEnabled,
  isRateLimitThresholdExceeded = false,
  readonly,
  isBeingDeleted
}) => {
  const autoRepoDiscoveryFF = useAutoRepoDiscoveryFF(sensor_name);

  const shouldDisplayAutoMonitoredIndicator =
    !!autoRepoDiscoveryConfig?.isActive && autoRepoDiscoveryFF;

  return (
    <Flex items='center' grow='1'>
      <GitProviderDescription
        sensorName={sensor_name}
        name={providerName}
        integrationId={integrationId}
        gitIntegrations={gitIntegrations}
        updateSensorAuthorization={updateSensorAuthorization}
        readonly={readonly}
      />
      {isRateLimitThresholdExceeded && (
        <Tooltip title='This integration has recently encountered rate limit errors, which may impact data consistency and accuracy. You may want to consider using the multi tokens solution. Please reach out to our support team for assistance'>
          <Stack>
            <AlertIcon color={lightColorPalette.lbOrange_100} />
          </Stack>
        </Tooltip>
      )}
      <Separator>|</Separator>
      <IntegrationState
        stateOK={stateOK}
        repositoriesNumber={repositoriesNumber}
      />
      {isBeingDeleted && (
        <>
          <Separator>|</Separator>
          <div className='flex items-center gap-1 text-sm text-muted-foreground'>
            <Icon name='Trash' color='torchRed' />
            This integration is being removed, which may take some time to
            complete.
          </div>
        </>
      )}
      {shouldDisplayAutoMonitoredIndicator && (
        <>
          <Separator>|</Separator>
          <AutoMonitoredIndicator />
        </>
      )}
      {showNotConnectedToGitStream && !readonly && (
        <>
          <Separator>|</Separator>
          <GitStreamConnectionState
            icon='WarningOutline'
            iconColor='torchRed'
            text='gitStream installed but not connected'
          />
        </>
      )}
      {showIsConnectedToGitStream && !readonly && (
        <>
          <Separator>|</Separator>
          <GitStreamConnectionState
            icon='successOutline'
            iconColor='malachite'
            text='connected to gitStream'
          />
        </>
      )}
      <IntegrationURL url={url} />
      {!isMultiTokenEnabled && !readonly && (
        <IntegrationReauthorize
          toggleModal={toggleModal}
          account={account}
          stateOK={stateOK}
          integrationId={integrationId}
          sensor_name={sensor_name}
          url={url}
          gitIntegrations={gitIntegrations}
        />
      )}
    </Flex>
  );
};
